// Signed, revocable TV board links (issue #16, US-43). A board opens from `#/board/<pickup point>?key=<key>` with no
// login and can only read. The key is an HMAC-SHA256 tag, under SESSION_SECRET, of the pickup point id and that pickup
// point's board secret: a random value kept in D1 (`pickup_points.board_secret`), never sent to a client. Revoking the
// link writes a new secret, so every older key stops matching; a leaked D1 row alone cannot forge a key, because the
// tag also needs SESSION_SECRET. Keys never expire on their own: a TV stays on for months, revocation is how a link dies.
// The key is 20 lowercase Crockford base32 characters (100 bits), so a manager can type it on a TV remote if needed;
// reading it forgives case, spaces and dashes, and the letters Crockford folds (i and l as 1, o as 0).

/** The rules every side of the board link shares: the query parameter, the check interval and the socket close code. */
export const BOARD_LINK = {
  /** The query parameter that carries the key, in the board's URL and on its two API calls. */
  param: 'key',
  /** Length of a key in characters (5 bits each). */
  keyLength: 20,
  /** The promise of US-43: once revoked, the old link stops within a minute. */
  revokeWithinMs: 60_000,
  /**
   * How often an open board re-reads its settings (`GET /board/:pp?key=`), which is also its link check: a 401 there
   * puts the screen on "link not valid". Revocation closes the board's socket at once; this poll is the backstop.
   */
  checkEveryMs: 30_000,
  /** WebSocket close code the pickup point's socket sends to boards when their link is revoked (4000-4999 is private use). */
  revokedCloseCode: 4401,
} as const;

/** What a request's key says: no key at all, a key that does not match (forged, mistyped or revoked), or a good one. */
export type BoardLinkCheck = 'ok' | 'missing' | 'invalid';

const enc = new TextEncoder();
const CROCKFORD = '0123456789abcdefghjkmnpqrstvwxyz';
const KEY_SHAPE = new RegExp(`^[${CROCKFORD}]{${BOARD_LINK.keyLength}}$`);

function base32(bytes: Uint8Array, chars: number): string {
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const b of bytes) {
    buffer = (buffer << 8) | b;
    bits += 8;
    while (bits >= 5 && out.length < chars) {
      out += CROCKFORD[(buffer >> (bits - 5)) & 31];
      bits -= 5;
    }
    buffer &= (1 << bits) - 1;
    if (out.length >= chars) break;
  }
  return out;
}

function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** A fresh board secret for a pickup point: 128 random bits, hex. Stored in D1, mixed into the key, never shown. */
export function newBoardSecret(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * The board key of a pickup point. `boardSecret` null (a row written before migration 0010 backfilled it, or a test
 * fixture) counts as the empty secret: the key is still bound to SESSION_SECRET, and the first revocation sets a real one.
 */
export async function boardLinkKey(sessionSecret: string, pickupPointId: string, boardSecret: string | null): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(sessionSecret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const mac = await crypto.subtle.sign('HMAC', key, enc.encode(`board:${pickupPointId}:${boardSecret ?? ''}`));
  return base32(new Uint8Array(mac), BOARD_LINK.keyLength);
}

/** A key as typed or pasted: case, spaces and dashes ignored, i and l read as 1, o as 0 (Crockford). Null when it cannot be a key. */
export function normalizeBoardKey(input: string | null | undefined): string | null {
  if (typeof input !== 'string') return null;
  const k = input.toLowerCase().replace(/[\s-]/g, '').replace(/[il]/g, '1').replace(/o/g, '0');
  return KEY_SHAPE.test(k) ? k : null;
}

/** Check the key a board sent against the pickup point's current secret. An empty or absent key is `missing`. */
export async function checkBoardLinkKey(sessionSecret: string, pickupPointId: string, boardSecret: string | null, key: string | null | undefined): Promise<BoardLinkCheck> {
  if (key === null || key === undefined || key.trim() === '') return 'missing';
  const k = normalizeBoardKey(key);
  if (!k) return 'invalid';
  return constantTimeEqual(await boardLinkKey(sessionSecret, pickupPointId, boardSecret), k) ? 'ok' : 'invalid';
}

/** The board's address in the web app: `<web base>/#/board/<pickup point>?key=<key>`. */
export function boardLinkUrl(webBaseUrl: string, pickupPointId: string, key: string): string {
  return `${webBaseUrl.replace(/\/+$/, '')}/#/board/${encodeURIComponent(pickupPointId)}?${BOARD_LINK.param}=${encodeURIComponent(key)}`;
}

/** The pickup point and the key of a board route (`#/board/<id>?key=<key>`); `key` is null when the URL carries none. Null for any other route. */
export function parseBoardRoute(hash: string): { pickupPointId: string; key: string | null } | null {
  const m = /^#\/board\/([^/?#]+)\/?(?:\?(.*))?$/.exec(hash);
  if (!m) return null;
  let pickupPointId: string;
  try {
    pickupPointId = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  const raw = new URLSearchParams(m[2] ?? '').get(BOARD_LINK.param);
  const key = raw && raw.trim() !== '' ? raw.trim() : null;
  return { pickupPointId, key };
}
